import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Checkbox,
  Flex,
  Group,
  Loader,
  Pagination,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  UnstyledButton,
  ActionIcon,
  Tooltip,
  Box,
  useMantineTheme,
  useMantineColorScheme,
} from '@mantine/core';
import { IconSearch, IconSettings, IconGripVertical, IconArrowUp, IconArrowDown, IconClock } from '@tabler/icons-react';
import { ReportVelocityDrawer } from '../CreateReport/ReportVelocityDrawer';
import { AggregateKeywordDrawer, AggregateKeywordParams } from './AggregateKeywordDrawer';
import { AggregateAdKeywordDrawer, AggregateAdKeywordParams } from './AggregateAdKeywordDrawer';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { visibleGoalMetrics } from './assists';
import { GradeCellTd, JourneyIconButton, metricDisplayLabel, CAMPAIGN_HIDE_SX, CampaignHideToggle, campaignHideKey, HiddenRowsToggle } from './shared';
import AggregatedReportsAPI from '../../api/AggregatedReportsApi';
import {
  GRADE_COLUMNS,
  NetworkRow,
  NetworkGoalDef,
  sourceChipColor,
} from './types';
import { SortableTh, useTableSort } from './sorting';
import {
  STICKY_WIDTHS,
  TRAFFIC_METRIC_SORT_FIELD as TRAFFIC_SORT_FIELD,
  GOAL_METRIC_SORT_FIELD,
  trafficCell,
  renderGoalCell,
  cellBody,
  useMetricColumnOrder,
} from './AdNetworkTab';

// Metrics without a sort token (e.g. Value Breakdown) are not sortable, as on the ad-network tables.
const goalMetricSortField = (goalKey: string, metric: string) =>
  GOAL_METRIC_SORT_FIELD[metric] ? `goal:${goalKey}:${GOAL_METRIC_SORT_FIELD[metric]}` : '';

const PAGE_SIZE = 25;

type Level = 'campaign' | 'adset' | 'ad';

type Props = {
  enabled: boolean;
  funnelId: string | null;
  baseParams: Record<string, any>;
  breakdownType?: 'none' | 'device' | 'hour' | 'cust_type';
  trafficMetrics: string[];
  trafficFunnelMetricsByGoal: Record<string, string[]>;
  trafficGoalsEnabled: Record<string, boolean>;
  onGoalsChange?: (goals: NetworkGoalDef[]) => void;
  openSettings: (context: string, scope: string) => void;
  onJourneyOpen?: (args: {
    row: NetworkRow;
    goalKey: string;
    goalLabel: string;
    level: Level;
    parent_campaign_id: string;
    parent_adset_id: string;
  }) => void;
  // Same shape the Campaign / Ad Set / Ad tables use; drives Velocity and both Keyword Views.
  velocityContext?: {
    report_id: string;
    visiopt_campaign_id: string[];
    attribution_model: string;
    attribute_window_type: string;
    lookback_days: number;
    report_time: string;
    ad_account_id?: string[];
    // The report's full campaign selection; decides global vs per-campaign attribution.
    filter_visiopt_campaign_ids?: string[];
    // Every drawer this table opens counts the row it was opened from, Test Variation included.
    test_variation_enabled?: number;
  };
  onKeywordExport?: () => Promise<string>;
  // Session-wide hidden campaigns, shared with the Ad Network tabs.
  hiddenCampaignKeys: string[];
  onToggleHiddenCampaign: (key: string) => void;
};

// Assisted / Influenced Revenue live on the Ad Network Ad tab alone; drop them wherever they were picked.
const visibleForGoal = (_goalName: string, metrics: string[]): string[] =>
  visibleGoalMetrics(metrics, { isAdLevel: false, goalHasRevenue: false });

const LEVEL_LABELS: Record<Level, { single: string; plural: string; colHeader: string }> = {
  campaign: { single: 'campaign', plural: 'campaigns', colHeader: 'Campaign' },
  adset: { single: 'ad set', plural: 'ad sets', colHeader: 'Ad Set' },
  ad: { single: 'ad', plural: 'ads', colHeader: 'Ad' },
};

export function FunnelTrafficBreakdown({
  enabled,
  funnelId,
  baseParams,
  breakdownType = 'none',
  trafficMetrics,
  trafficFunnelMetricsByGoal,
  trafficGoalsEnabled,
  onGoalsChange,
  openSettings,
  onJourneyOpen,
  velocityContext,
  onKeywordExport,
  hiddenCampaignKeys,
  onToggleHiddenCampaign,
}: Props) {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();

  const [level, setLevel] = useState<Level>('campaign');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [parentCampaignIds, setParentCampaignIds] = useState<string[]>([]);
  const [parentAdsetIds, setParentAdsetIds]       = useState<string[]>([]);

  const [selectedCampaignIds, setSelectedCampaignIds] = useState<string[]>([]);
  const [selectedAdsetIds, setSelectedAdsetIds]       = useState<string[]>([]);

  const [rows, setRows] = useState<NetworkRow[]>([]);
  const [serverHiddenRows, setServerHiddenRows] = useState<NetworkRow[]>([]);
  const [goals, setGoals] = useState<NetworkGoalDef[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages]     = useState(1);
  const [loading, setLoading] = useState(false);
  const { sort, toggle: toggleSort } = useTableSort();

  const [keywordOpen, setKeywordOpen] = useState(false);
  const [keywordParams, setKeywordParams] = useState<AggregateKeywordParams | null>(null);
  const [adKeywordOpen, setAdKeywordOpen] = useState(false);
  const [adKeywordParams, setAdKeywordParams] = useState<AggregateAdKeywordParams | null>(null);
  const [velocityOpen, setVelocityOpen] = useState(false);
  const [velocityParams, setVelocityParams] = useState<Record<string, any>>({});
  const [velocityDisplayName, setVelocityDisplayName] = useState('');
  const [attributionUsed, setAttributionUsed] = useState('');
  const [hiddenOpen, setHiddenOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [debouncedSearch]);

  useEffect(() => {
    setLevel('campaign');
    setPage(1);
    setSearch('');
    setDebouncedSearch('');
    setParentCampaignIds([]);
    setParentAdsetIds([]);
    setSelectedCampaignIds([]);
    setSelectedAdsetIds([]);
    setRows([]);
    setServerHiddenRows([]);
    setTotalRecords(0);
    setTotalPages(1);
  }, [funnelId]);

  const levelKey = `${level}|${parentCampaignIds.join(',')}|${parentAdsetIds.join(',')}`;
  const lastLevelKey = useRef(levelKey);
  useEffect(() => {
    if (lastLevelKey.current === levelKey) { return; }
    lastLevelKey.current = levelKey;
    setRows([]);
    setServerHiddenRows([]);
    setTotalRecords(0);
    setTotalPages(1);
    if (enabled && funnelId) { setLoading(true); }
  }, [levelKey, enabled, funnelId]);

  // Race-safe fetch: only the latest request commits state.
  const requestSeq = useRef(0);
  useEffect(() => {
    if (!enabled || !funnelId) { return; }
    const seq = ++requestSeq.current;
    setLoading(true);
    AggregatedReportsAPI
      .getfunneltraffic({
        ...baseParams,
        level,
        page,
        per_page: PAGE_SIZE,
        search: debouncedSearch,
        parent_campaign_id: parentCampaignIds,
        parent_adset_id: parentAdsetIds,
        sort_field: sort?.field ?? '',
        sort_dir:   sort?.dir   ?? 'asc',
        hidden_campaigns: hiddenCampaignKeys,
      })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (seq !== requestSeq.current) { return; }
        if (data && data.status === 1 && data.data) {
          setRows(Array.isArray(data.data.rows) ? (data.data.rows as NetworkRow[]) : []);
          setServerHiddenRows(Array.isArray(data.data.hidden_rows) ? (data.data.hidden_rows as NetworkRow[]) : []);
          setAttributionUsed(typeof data.data.attribution_used === 'string' ? data.data.attribution_used : '');
          if (Array.isArray(data.data.goals)) {
            setGoals(data.data.goals as NetworkGoalDef[]);
            onGoalsChange?.(data.data.goals as NetworkGoalDef[]);
          }
          const pg = data.data.pagination || {};
          setTotalRecords(typeof pg.total_records === 'number' ? pg.total_records : 0);
          setTotalPages(typeof pg.total_pages === 'number' && pg.total_pages > 0 ? pg.total_pages : 1);
        } else {
          setRows([]);
          setServerHiddenRows([]);
          setTotalRecords(0);
          setTotalPages(1);
        }
      })
      .catch(() => {
        if (seq !== requestSeq.current) { return; }
        setRows([]);
        setServerHiddenRows([]);
        setTotalRecords(0);
        setTotalPages(1);
      })
      .finally(() => {
        if (seq !== requestSeq.current) { return; }
        setLoading(false);
      });
  }, [enabled, funnelId, level, page, debouncedSearch, parentCampaignIds.join('|'), parentAdsetIds.join('|'), sort?.field, sort?.dir, baseParams]);

  // Drill helpers — prefer multi-selection over the clicked row when entering the next level.
  const drillIntoAdsets = (clickedCampaignId?: string) => {
    const ids = selectedCampaignIds.length > 0
      ? selectedCampaignIds
      : (clickedCampaignId ? [clickedCampaignId] : []);
    setParentCampaignIds(ids);
    setParentAdsetIds([]);
    setSelectedAdsetIds([]);
    setSearch('');
    setDebouncedSearch('');
    setPage(1);
    setLevel('adset');
  };

  const drillIntoAds = (clickedAdsetId?: string) => {
    const ids = selectedAdsetIds.length > 0
      ? selectedAdsetIds
      : (clickedAdsetId ? [clickedAdsetId] : []);
    setParentAdsetIds(ids);
    setSearch('');
    setDebouncedSearch('');
    setPage(1);
    setLevel('ad');
  };

  const goToLevel = (target: Level) => {
    if (target === 'campaign') {
      setParentCampaignIds([]);
      setParentAdsetIds([]);
    } else if (target === 'adset') {
      setParentAdsetIds([]);
    }
    setSearch('');
    setDebouncedSearch('');
    setPage(1);
    setLevel(target);
  };

  const activeSelectedIds =
    level === 'campaign'
      ? selectedCampaignIds
      : level === 'adset'
      ? selectedAdsetIds
      : ([] as string[]);

  const helperText = (() => {
    const labels = LEVEL_LABELS[level];
    const n = activeSelectedIds.length;
    if (level === 'ad') {
      return n > 0
        ? `${n} ad${n > 1 ? 's' : ''} selected`
        : 'Individual ad creatives for the current drill-down';
    }
    const nextLabel = level === 'campaign' ? 'ad sets' : 'ads';
    if (n > 0) {
      return `${n} ${n > 1 ? labels.plural : labels.single} selected · click a name to drill into ${
        n > 1 ? 'their' : 'its'
      } ${nextLabel}`;
    }
    return `Click a ${labels.single} name to drill into its ${nextLabel}`;
  })();

  const LevelTab = ({
    value,
    label,
    count,
  }: {
    value: Level;
    label: string;
    count: number;
  }) => {
    const active = level === value;
    return (
      <UnstyledButton
        onClick={() => goToLevel(value)}
        sx={(t) => ({
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 14px',
          borderRadius: 5,
          fontSize: 13,
          fontWeight: 600,
          color: active
            ? t.colors.teal[t.colorScheme === 'dark' ? 3 : 7]
            : t.colorScheme === 'dark'
            ? t.colors.dark[1]
            : t.colors.gray[6],
          backgroundColor: active
            ? t.colorScheme === 'dark'
              ? t.fn.rgba(t.colors.teal[6], 0.15)
              : t.colors.teal[0]
            : 'transparent',
          border: `1px solid ${
            active
              ? t.colors.teal[t.colorScheme === 'dark' ? 6 : 4]
              : t.colorScheme === 'dark'
              ? t.colors.dark[4]
              : t.colors.gray[3]
          }`,
          transition: 'background-color 120ms ease, color 120ms ease',
          '&:hover': {
            backgroundColor: active
              ? undefined
              : t.colorScheme === 'dark'
              ? t.colors.dark[5]
              : t.colors.gray[1],
          },
        })}
      >
        {label}
        {count > 0 && (
          <Badge
            size="xs"
            variant="filled"
            color="teal"
            sx={{ minWidth: 18, height: 18, padding: '0 6px' }}
          >
            {count}
          </Badge>
        )}
      </UnstyledButton>
    );
  };

  const leadColumns: { key: string; label: string }[] = useMemo(() => {
    if (level === 'campaign') return [{ key: 'campaign', label: 'Campaign' }];
    if (level === 'adset')
      return [
        { key: 'campaign', label: 'Campaign' },
        { key: 'adset', label: 'Ad Set' },
      ];
    return [
      { key: 'campaign', label: 'Campaign' },
      { key: 'adset', label: 'Ad Set' },
      { key: 'ad', label: 'Ad' },
    ];
  }, [level]);

  // Same goal rule, column order and drag-to-reorder as the Campaign / Ad Set / Ad tables, minus
  // the assist columns: those are an Ad-tab metric this table never computes.
  const activeGoals = useMemo(
    () => goals.filter((g) => {
      const enabled = trafficGoalsEnabled[g.name];
      return (enabled === undefined ? true : enabled)
        && visibleForGoal(g.name, trafficFunnelMetricsByGoal[g.name] ?? []).length > 0;
    }),
    [goals, trafficGoalsEnabled, trafficFunnelMetricsByGoal]
  );
  const { orderedTrafficMetrics, goalMetricsOrdered, handleDragEnd } =
    useMetricColumnOrder(trafficMetrics, trafficFunnelMetricsByGoal, activeGoals, visibleForGoal);

  // Google's advertisingChannelType. Structural like Source and the name columns - never
  // selectable - and only present when a Google row is in view, as on the ad-network tabs.
  const showPlacement = rows.some((r) => r.source === 'google') || serverHiddenRows.some((r) => r.source === 'google');

  const cellBorderColor = theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3];
  const headerBg       = theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[1];
  const groupDividerColor = theme.colorScheme === 'dark' ? theme.colors.dark[2] : theme.colors.gray[5];
  const groupDividerStyle: React.CSSProperties = { borderLeft: `2px solid ${groupDividerColor}` };
  const goalDividerStyle: React.CSSProperties  = { borderLeft: `2px solid ${groupDividerColor}` };

  // An ad is the last level, so there is nothing to select for a further drill-down.
  const hasCheckboxColumn = level !== 'ad';
  const nameCellStyle: React.CSSProperties = { overflowWrap: 'anywhere', whiteSpace: 'normal', verticalAlign: 'top' };

  // Identity columns stay pinned while the metrics scroll, at the Campaign / Ad Set / Ad tables' widths; Placement scrolls.
  const stickyKeys: string[] = [
    ...(hasCheckboxColumn ? ['checkbox'] : []),
    'source',
    ...leadColumns.map((c) => c.key),
  ];
  const stickyLeft: Record<string, number> = {};
  stickyKeys.reduce((left, key) => {
    stickyLeft[key] = left;
    return left + (STICKY_WIDTHS[key] ?? 0);
  }, 0);
  const stickyBg = theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0];
  const stickySubBg = theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1];
  // Header cells sit above body cells so a pinned header covers the rows scrolling under it.
  // Collapsed column borders stay with the table grid, so a pinned cell draws its own divider instead.
  const stickyCell = (key: string, zIndex: number, extra?: React.CSSProperties): React.CSSProperties => ({
    position: 'sticky',
    left: stickyLeft[key],
    zIndex,
    background: stickyBg,
    width: STICKY_WIDTHS[key],
    minWidth: STICKY_WIDTHS[key],
    maxWidth: STICKY_WIDTHS[key],
    overflow: 'hidden',
    borderRight: 'none',
    boxShadow: key === 'checkbox' ? 'none' : `inset -1px 0 0 ${cellBorderColor}`,
    ...extra,
  });

  const keywordBtnStyle: React.CSSProperties = colorScheme === 'dark'
    ? { fontSize: 10, padding: '2px 8px', border: '1px solid #91a7ff', borderRadius: 5, background: '#1f3a70', color: '#dbe4ff', cursor: 'pointer', whiteSpace: 'nowrap', flex: '0 0 auto' }
    : { fontSize: 10, padding: '2px 8px', border: '1px solid #4c6ef5', borderRadius: 5, background: 'transparent', color: '#4c6ef5', cursor: 'pointer', whiteSpace: 'nowrap', flex: '0 0 auto' };

  // Ad-group keywords come from Google, so only a Google row carrying an ad group id can open them.
  const adgroupKeywordButton = (row: NetworkRow, adgroupId: string, adgroupName: string) => {
    if (row.source !== 'google' || !adgroupId || !velocityContext) { return null; }
    return (
      <button
        style={keywordBtnStyle}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          setKeywordParams({
            visiopt_campaign_id: velocityContext.visiopt_campaign_id,
            adset_id: adgroupId,
            adset_name: adgroupName,
            report_time: velocityContext.report_time,
            ad_account_id: velocityContext.ad_account_id ?? [],
          });
          setKeywordOpen(true);
        }}
      >
        Keyword View
      </button>
    );
  };

  /* Per-ad keywords read the {keyword} ValueTrack Google writes into the landing URL, and Google
     fills it on Search clicks only. A blank placement means the channel was never synced. */
  const adKeywordButton = (row: NetworkRow, adName: string) => {
    const channel = (row.placement ?? '').trim().toUpperCase();
    if (row.source !== 'google' || !row.id || !velocityContext || (channel !== '' && channel !== 'SEARCH')) { return null; }
    return (
      <button
        style={keywordBtnStyle}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          setAdKeywordParams({
            visiopt_campaign_id: velocityContext.visiopt_campaign_id,
            filter_visiopt_campaign_ids: velocityContext.filter_visiopt_campaign_ids ?? velocityContext.visiopt_campaign_id,
            ad_id: row.id,
            ad_name: adName,
            attribution_model: velocityContext.attribution_model,
            report_time: velocityContext.report_time,
            test_variation_enabled: velocityContext.test_variation_enabled ?? 0,
          });
          setAdKeywordOpen(true);
        }}
      >
        Keyword View
      </button>
    );
  };

  // Parent first, drilled level last and prominent, as on the Campaign / Ad Set / Ad tables.
  const renderHierarchy = (row: NetworkRow, lvl: Level, drillCb?: () => void) => {
    const prom = { fontWeight: 700, fontSize: 14 } as const;
    const dim  = { fontSize: 12, color: theme.colorScheme === 'dark' ? theme.colors.dark[2] : theme.colors.gray[6] } as const;
    const name = (label: string | undefined) => (drillCb
      ? <UnstyledButton onClick={drillCb} sx={prom}>{label}</UnstyledButton>
      : <Text sx={prom}>{label}</Text>);
    const hideKey = hideKeyOf(row);
    const hideToggle = hideKey !== ''
      ? <CampaignHideToggle hidden={hiddenKeySet.has(hideKey)} onToggle={() => onToggleHiddenCampaign(hideKey)} />
      : null;
    if (lvl === 'campaign') {
      // ChatGPT, Organic and Direct are single aggregate rows with no campaign name.
      if (['chatgpt', 'organic', 'direct'].includes(String(row.source || '').toLowerCase())) {
        return <td style={{ ...nameCellStyle, ...stickyCell('campaign', 2), verticalAlign: 'middle', textAlign: 'center' }}>-</td>;
      }
      return (
        <td style={{ ...nameCellStyle, ...stickyCell('campaign', 2) }}>
          <Flex align="center" columnGap={8} sx={{ width: '100%' }}>
            <Box sx={{ flex: '1 1 auto', minWidth: 0, '& > *': { maxWidth: '100%' } }}>{name(row.campaign ?? row.entity_name)}</Box>
            {hideToggle}
          </Flex>
        </td>
      );
    }
    if (lvl === 'adset') {
      return (
        <>
          <td style={{ ...nameCellStyle, ...stickyCell('campaign', 2) }}>
          <Flex align="center" columnGap={8} sx={{ width: '100%' }}>
            <Text sx={{ ...dim, flex: '1 1 auto', minWidth: 0 }}>{row.campaign ?? '-'}</Text>
            {hideToggle}
          </Flex>
        </td>
          <td style={{ ...nameCellStyle, ...stickyCell('adset', 2) }}>
            <Flex align="flex-start" wrap="wrap" columnGap={8} rowGap={4} sx={{ width: '100%' }}>
              <Box sx={{ flex: '0 1 auto', minWidth: 0, overflowWrap: 'anywhere' }}>{name(row.adset ?? row.entity_name)}</Box>
              {adgroupKeywordButton(row, row.id, (row.adset ?? row.entity_name) ?? '')}
            </Flex>
          </td>
        </>
      );
    }
    return (
      <>
        <td style={{ ...nameCellStyle, ...stickyCell('campaign', 2) }}>
          <Flex align="center" columnGap={8} sx={{ width: '100%' }}>
            <Text sx={{ ...dim, flex: '1 1 auto', minWidth: 0 }}>{row.campaign ?? '-'}</Text>
            {hideToggle}
          </Flex>
        </td>
        <td style={{ ...nameCellStyle, ...stickyCell('adset', 2) }}>
          <Flex align="flex-start" wrap="wrap" columnGap={8} rowGap={4} sx={{ width: '100%' }}>
            <Box sx={{ flex: '0 1 auto', minWidth: 0, overflowWrap: 'anywhere' }}><Text sx={dim}>{row.adset ?? '-'}</Text></Box>
            {adgroupKeywordButton(row, row.parent_adset_id ?? '', row.adset ?? '')}
          </Flex>
        </td>
        <td style={{ ...nameCellStyle, ...stickyCell('ad', 2) }}>
          <Flex align="flex-start" wrap="wrap" columnGap={8} rowGap={4} sx={{ width: '100%' }}>
            <Box sx={{ flex: '0 1 auto', minWidth: 0, overflowWrap: 'anywhere' }}><Text sx={prom}>{row.ad ?? row.entity_name}</Text></Box>
            {adKeywordButton(row, (row.ad ?? row.entity_name) ?? '')}
          </Flex>
        </td>
      </>
    );
  };

  const renderPlacementCell = (row: NetworkRow) => (
    showPlacement ? (
      <td>
        <Text sx={{ fontSize: 12, color: theme.colorScheme === 'dark' ? theme.colors.dark[2] : theme.colors.gray[6] }}>
          {row.placement ? row.placement : '-'}
        </Text>
      </td>
    ) : null
  );

  const renderMetricCells = (row: NetworkRow) => (
    <>
      {orderedTrafficMetrics.map((m, i) => (
        GRADE_COLUMNS.includes(m)
          ? <GradeCellTd key={m} grade={row.grade} reason={row.grade_reason} column={m} tdStyle={i === 0 ? groupDividerStyle : undefined} />
          : (
            <td key={m} style={i === 0 ? groupDividerStyle : undefined}>
              {cellBody(trafficCell(row, m))}
            </td>
          )
      ))}
      {activeGoals.flatMap((goal) => {
        const g = row.goals?.[goal.key];
        return goalMetricsOrdered(goal.name).map((m, i) => {
          const baseStyle = i === 0 ? goalDividerStyle : undefined;
          const cellValue = renderGoalCell(g, m);
          // The count stays plain text; a route icon beside it opens the Customer Journey drawer.
          const isConversions = m === 'Conversions';
          const convCount = g ? Number(g.conversions ?? 0) : 0;
          const journeyClickable = isConversions && !!onJourneyOpen && convCount > 0;
          return (
            <td key={`${goal.key}-${m}`} style={baseStyle}>
              {journeyClickable ? (
                <Flex align="center" gap={6}>
                  {cellValue}
                  <JourneyIconButton
                    onClick={() =>
                      onJourneyOpen!({
                        row,
                        goalKey: goal.key,
                        goalLabel: goal.name,
                        level,
                        parent_campaign_id: row.parent_campaign_id ?? '',
                        parent_adset_id: row.parent_adset_id ?? '',
                      })
                    }
                  />
                  {/* Velocity is per ad, so Organic / Direct rows have none. */}
                  {velocityContext && row.source !== 'organic' && row.source !== 'direct' && (
                    <Tooltip label="View velocity report" withArrow withinPortal>
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        color="indigo"
                        aria-label="View velocity report"
                        onClick={() => {
                          setVelocityParams({
                            report_id: velocityContext.report_id,
                            visiopt_campaign_id: velocityContext.visiopt_campaign_id,
                            filter_visiopt_campaign_ids: velocityContext.filter_visiopt_campaign_ids ?? velocityContext.visiopt_campaign_id,
                            attribution_model: velocityContext.attribution_model,
                            report_time: velocityContext.report_time,
                            attribute_window_type: velocityContext.attribute_window_type,
                            lookback_days: velocityContext.lookback_days,
                            test_variation_enabled: velocityContext.test_variation_enabled ?? 0,
                            traffic_source: row.source,
                            goal_key: goal.key,
                            level,
                            ad_entity_id: row.id,
                            parent_campaign_id: row.parent_campaign_id ?? '',
                            parent_adset_id: row.parent_adset_id ?? '',
                          });
                          setVelocityDisplayName((row.ad ?? row.adset ?? row.campaign ?? row.entity_name) ?? '');
                          setVelocityOpen(true);
                        }}
                      >
                        <IconClock size="0.95rem" />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Flex>
              ) : (
                cellValue
              )}
            </td>
          );
        });
      })}
    </>
  );

  // Device / Hour / Customer Type sub-rows, drawn as on the Campaign / Ad Set / Ad tables.
  const renderSubRows = (row: NetworkRow) => {
    const isCt = breakdownType === 'cust_type';
    const ctSupplied: any[] = Array.isArray(row.customer_type_breakdown) ? row.customer_type_breakdown : [];
    const subRows: any[] = (row.device_breakdown && row.device_breakdown.length > 0)
      ? row.device_breakdown
      : (row.hour_breakdown && row.hour_breakdown.length > 0)
      ? row.hour_breakdown
      : isCt
      ? ['First-time Customers', 'Returning Customers'].map((name) =>
        ctSupplied.find((x) => x && x.customer_type_name === name) ?? { customer_type_name: name })
      : [];
    if (subRows.length === 0) { return null; }
    const ctFlags = row.customer_type_eligible ?? {};
    const ctNa = (
      <span style={{ color: '#adb5bd', fontStyle: 'italic' }} title="Customer Type applies only to webhook goals with Count as Purchase enabled">-</span>
    );
    const subBg = { background: 'rgba(0,0,0,0.025)', fontStyle: 'italic' as const };
    const labelKey = leadColumns[leadColumns.length - 1].key;
    return subRows.map((sub, si) => (
      <tr key={`${row.id}-sub-${si}`}>
        {hasCheckboxColumn && <td style={{ ...subBg, ...stickyCell('checkbox', 2), background: stickySubBg }} />}
        <td style={{ ...subBg, ...stickyCell('source', 2), background: stickySubBg }} />
        {leadColumns.slice(0, -1).map((c) => (
          <td key={c.key} style={{ ...subBg, ...stickyCell(c.key, 2), background: stickySubBg }} />
        ))}
        <td style={{ ...subBg, ...stickyCell(labelKey, 2), background: stickySubBg, paddingLeft: 32, color: '#666' }}>
          ↳ {sub.device_name ?? sub.hour_label ?? sub.customer_type_name ?? ''}
        </td>
        {showPlacement && <td style={subBg} />}
        {orderedTrafficMetrics.map((m, i) => (
          GRADE_COLUMNS.includes(m)
            ? <GradeCellTd key={m} grade={sub.grade} reason={sub.grade_reason} column={m} tdStyle={{ ...subBg, ...(i === 0 ? groupDividerStyle : {}) }} />
            : <td key={m} style={{ ...subBg, ...(i === 0 ? groupDividerStyle : {}) }}>{isCt ? ctNa : cellBody(trafficCell(sub, m))}</td>
        ))}
        {activeGoals.flatMap((goal) =>
          goalMetricsOrdered(goal.name).map((m, i) => (
            <td key={`${goal.key}-${m}`} style={{ ...subBg, ...(i === 0 ? goalDividerStyle : {}) }}>
              {isCt && Number(ctFlags[goal.key] ?? 0) !== 1 ? ctNa : renderGoalCell(sub.goals?.[goal.key], m)}
            </td>
          ))
        )}
      </tr>
    ));
  };

  // Hidden rows are never selected, so a drill-down cannot carry them.
  const renderCheckbox = (checked: boolean, onChange: (v: boolean) => void, disabled = false) => (
    <td style={stickyCell('checkbox', 2)}>
      <Checkbox checked={checked} disabled={disabled} onChange={(e) => onChange(e.currentTarget.checked)} />
    </td>
  );

  // ChatGPT, Organic and Direct are single aggregate rows, so they are never hidden.
  const hideKeyOf = (row: NetworkRow) =>
    (['chatgpt', 'organic', 'direct'].includes(String(row.source || '').toLowerCase()) ? '' : campaignHideKey(row, level));
  const hiddenKeySet = new Set(hiddenCampaignKeys);
  const isHiddenRow = (row: NetworkRow) => hiddenKeySet.has(hideKeyOf(row));
  // Rows unhidden since the last fetch stay on this page until the next fetch places them.
  // Organic and Direct (no ad behind them) always close the table, Organic first, whatever the sort.
  const visibleRows = (() => {
    const onPage = new Set(rows.map((r) => `${r.source}:${r.id}`));
    const shown = [
      ...rows.filter((r) => !isHiddenRow(r)),
      ...serverHiddenRows.filter((r) => !onPage.has(`${r.source}:${r.id}`) && !isHiddenRow(r)),
    ];
    return [
      ...shown.filter((r) => r.source !== 'organic' && r.source !== 'direct'),
      ...shown.filter((r) => r.source === 'organic'),
      ...shown.filter((r) => r.source === 'direct'),
    ];
  })();
  // Hidden rows from every page (server) plus rows hidden on this page since the last fetch.
  const hiddenRows = (() => {
    const seen = new Set<string>();
    return [...serverHiddenRows, ...rows].filter((r) => {
      const rowKey = `${r.source}:${r.id}`;
      if (seen.has(rowKey) || !isHiddenRow(r)) { return false; }
      seen.add(rowKey);
      return true;
    });
  })();

  /* Select-all for the level on screen. ChatGPT, Organic and Direct have no ad sets, so they stay out
     of the selection exactly as their own rows do. */
  const selectableRowIds = visibleRows
    .filter((r) => level !== 'campaign' || !['chatgpt', 'organic', 'direct'].includes(String(r.source || '').toLowerCase()))
    .map((r) => r.id);
  const levelSelection = level === 'adset' ? selectedAdsetIds : selectedCampaignIds;
  const setLevelSelection = level === 'adset' ? setSelectedAdsetIds : setSelectedCampaignIds;
  const allRowsSelected = selectableRowIds.length > 0
    && selectableRowIds.every((id) => levelSelection.includes(id));
  const someRowsSelected = !allRowsSelected && selectableRowIds.some((id) => levelSelection.includes(id));

  // A hidden campaign's rows drop out of the selection so drill-downs never carry them.
  useEffect(() => {
    const hiddenIds = hiddenRows.map((r) => r.id);
    if (level === 'ad' || hiddenIds.length === 0) { return; }
    setLevelSelection((prev) => {
      const next = prev.filter((id) => !hiddenIds.includes(id));
      return next.length === prev.length ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hiddenCampaignKeys, rows]);

  const totalCols = (hasCheckboxColumn ? 1 : 0) + 1 /* source */ + leadColumns.length + (showPlacement ? 1 : 0)
    + orderedTrafficMetrics.length
    + activeGoals.reduce((sum, g) => sum + goalMetricsOrdered(g.name).length, 0);

  // One flat header list in render order, so indices line up with the drag handler's sections.
  const leafHeaders = [
    ...orderedTrafficMetrics.map((m, i) => ({
      id: `traffic:${m}`, metric: m, sortField: TRAFFIC_SORT_FIELD[m] ?? '', divider: i === 0,
    })),
    ...activeGoals.flatMap((goal) => goalMetricsOrdered(goal.name).map((m, i) => ({
      id: `goal:${goal.key}:${m}`, metric: m, sortField: goalMetricSortField(goal.key, m), divider: i === 0,
    }))),
  ];

  const renderRows = (list: NetworkRow[]) => {
    if (level === 'campaign') {
      return list.map((row) => {
        const checked = selectedCampaignIds.includes(row.id);
        // ChatGPT, Organic and Direct are aggregate-only (no ad sets / ads): no checkbox, no drill-down.
        const isCgpt = ['chatgpt', 'organic', 'direct'].includes(String(row.source || '').toLowerCase());
        return (
          <Fragment key={row.id}>
          <tr>
            {isCgpt ? (
              <td style={stickyCell('checkbox', 2)} />
            ) : (
              renderCheckbox(checked, (v) =>
                setSelectedCampaignIds((prev) => (v ? [...prev, row.id] : prev.filter((id) => id !== row.id))), isHiddenRow(row)
              )
            )}
            <td style={stickyCell('source', 2)}>
              <Badge color={sourceChipColor(row.source_label)} variant={row.source === 'chatgpt' ? 'outline' : 'light'} radius="sm">
                {row.source_label}
              </Badge>
            </td>
            {renderHierarchy(row, 'campaign', isCgpt ? undefined : () => drillIntoAdsets(row.id))}
            {renderPlacementCell(row)}
            {renderMetricCells(row)}
          </tr>
          {renderSubRows(row)}
          </Fragment>
        );
      });
    }
    if (level === 'adset') {
      return list.map((row) => {
        const checked = selectedAdsetIds.includes(row.id);
        return (
          <Fragment key={row.id}>
          <tr>
            {renderCheckbox(checked, (v) =>
              setSelectedAdsetIds((prev) => (v ? [...prev, row.id] : prev.filter((id) => id !== row.id))), isHiddenRow(row)
            )}
            <td style={stickyCell('source', 2)}>
              <Badge color={sourceChipColor(row.source_label)} variant="light" radius="sm">
                {row.source_label}
              </Badge>
            </td>
            {renderHierarchy(row, 'adset', () => drillIntoAds(row.id))}
            {renderPlacementCell(row)}
            {renderMetricCells(row)}
          </tr>
          {renderSubRows(row)}
          </Fragment>
        );
      });
    }
    return list.map((row) => (
      <Fragment key={row.id}>
      <tr>
        <td style={stickyCell('source', 2)}>
          <Badge color={sourceChipColor(row.source_label)} variant="light" radius="sm">
            {row.source_label}
          </Badge>
        </td>
        {renderHierarchy(row, 'ad')}
        {renderPlacementCell(row)}
        {renderMetricCells(row)}
      </tr>
      {renderSubRows(row)}
      </Fragment>
    ));
  };

  return (
    <Paper
      withBorder
      radius="md"
      p="md"
      mt="sm"
      sx={(t) => ({
        backgroundColor: t.colorScheme === 'dark' ? t.colors.dark[6] : t.colors.gray[0],
        position: 'relative',
      })}
    >
      <Flex justify="space-between" align="flex-start" wrap="wrap" gap="sm" mb="sm">
        <Stack spacing={4}>
          <Flex gap="xs" wrap="wrap">
            <LevelTab value="campaign" label="Campaign" count={selectedCampaignIds.length} />
            <LevelTab value="adset" label="Ad Set" count={selectedAdsetIds.length} />
            <LevelTab value="ad" label="Ad" count={0} />
          </Flex>
          <Text size="xs" color="dimmed">Raw network spend · filtered to this funnel</Text>
        </Stack>
        <ActionIcon
          variant="default"
          size="md"
          title="Traffic Breakdown Settings"
          onClick={() => openSettings(
            level === 'campaign' ? 'Campaign Settings' : level === 'adset' ? 'Ad Set Settings' : 'Ad Settings',
            'Applies to Funnel · Traffic Breakdown'
          )}
        >
          <IconSettings size="1rem" />
        </ActionIcon>
      </Flex>

      <Stack spacing={6} mb="sm">
        <TextInput
          size="sm"
          placeholder={`Search ${LEVEL_LABELS[level].plural}...`}
          icon={<IconSearch size="0.9rem" />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
        <Text size="xs" color="dimmed">{helperText}</Text>
      </Stack>

      {visibleRows.length === 0 && hiddenRows.length === 0 ? (
        // Same empty state as the Ad Network tabs: one centred box in place of the table.
        <Paper
          withBorder
          radius="md"
          p="xl"
          sx={(t) => ({
            backgroundColor: t.colorScheme === 'dark' ? t.colors.dark[6] : t.colors.gray[0],
            textAlign: 'center',
          })}
        >
          {funnelId && loading ? (
            <Loader size="sm" />
          ) : (
            <Text color="dimmed" size="sm">
              {!funnelId
                ? 'Select a Visiopt campaign in the Funnel tab above to load traffic data.'
                : `No ${LEVEL_LABELS[level].plural} match the current filters.`}
            </Text>
          )}
        </Paper>
      ) : (
      <ScrollArea type="auto" offsetScrollbars>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Table
            verticalSpacing="sm"
            horizontalSpacing="md"
            withBorder
            withColumnBorders
            sx={{
              minWidth: Math.max(1200, 560 + leadColumns.length * 240 + goals.length * 360),
              borderColor: cellBorderColor,
              'thead tr th': { backgroundColor: headerBg, borderColor: cellBorderColor },
              'tbody tr td': { borderColor: cellBorderColor },
              ...CAMPAIGN_HIDE_SX,
            }}
          >
            <thead>
              <tr>
                {hasCheckboxColumn && (
                  <th rowSpan={2} style={stickyCell('checkbox', 4, { background: headerBg })}>
                    <Tooltip
                      withArrow
                      withinPortal
                      label={allRowsSelected
                        ? `Clear all ${level === 'adset' ? 'ad sets' : 'campaigns'} on this page`
                        : `Select all ${level === 'adset' ? 'ad sets' : 'campaigns'} on this page`}
                    >
                      <Checkbox
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        disabled={selectableRowIds.length === 0}
                        aria-label="Select all rows"
                        onChange={(e) => setLevelSelection(e.currentTarget.checked ? selectableRowIds : [])}
                      />
                    </Tooltip>
                  </th>
                )}
                <SortableTh field="source" sort={sort} onToggle={toggleSort} rowSpan={2} thStyle={stickyCell('source', 4, { background: headerBg })}>Source</SortableTh>
                {leadColumns.map((c) => (
                  <SortableTh
                    key={c.key}
                    field={c.key}
                    sort={sort}
                    onToggle={toggleSort}
                    rowSpan={2}
                    thStyle={stickyCell(c.key, 4, { background: headerBg })}
                  >
                    {c.label}
                  </SortableTh>
                ))}
                {showPlacement && (
                  <th rowSpan={2}>
                    <Text size="xs" color="dimmed" transform="uppercase" weight={600}>Placement</Text>
                  </th>
                )}
                <th colSpan={orderedTrafficMetrics.length} style={groupDividerStyle}>
                  <Text size="xs" color="blue" transform="uppercase" weight={700} align="center">Traffic</Text>
                </th>
                {activeGoals.map((goal) => (
                  <th key={goal.key} colSpan={goalMetricsOrdered(goal.name).length} style={goalDividerStyle}>
                    <Text
                      size="xs"
                      transform="uppercase"
                      weight={700}
                      align="center"
                      sx={(t) => ({
                        color: goal.is_primary
                          ? t.colors.teal[t.colorScheme === 'dark' ? 3 : 7]
                          : t.colorScheme === 'dark' ? t.colors.dark[1] : t.colors.gray[7],
                      })}
                    >
                      {goal.name}
                    </Text>
                  </th>
                ))}
              </tr>
              {/* Drag a column header to reorder it within its group, as on the Campaign / Ad Set / Ad tables. */}
              <Droppable droppableId="funnel-leaf-headers" direction="horizontal" type="leaf-col">
                {(dropProvided) => (
                  <tr
                    ref={dropProvided.innerRef as unknown as React.Ref<HTMLTableRowElement>}
                    {...dropProvided.droppableProps}
                  >
                    {leafHeaders.map((h, i) => (
                      <Draggable key={h.id} draggableId={h.id} index={i}>
                        {(dragProvided, snapshot) => (
                          <th
                            ref={dragProvided.innerRef as unknown as React.Ref<HTMLTableCellElement>}
                            {...dragProvided.draggableProps}
                            style={{
                              ...(h.divider ? groupDividerStyle : {}),
                              ...(dragProvided.draggableProps.style as React.CSSProperties),
                              background: snapshot.isDragging ? headerBg : undefined,
                            }}
                          >
                            <Flex align="center" gap={4} justify="flex-end" sx={{ width: '100%' }}>
                              <span {...dragProvided.dragHandleProps} style={{ display: 'inline-flex', cursor: 'grab', flex: '0 0 auto' }}>
                                <IconGripVertical size="0.75rem" opacity={0.45} />
                              </span>
                              <UnstyledButton onClick={() => h.sortField && toggleSort(h.sortField)} disabled={!h.sortField}>
                                <Flex align="center" gap={4}>
                                  <Text size="xs" color="dimmed" transform="uppercase" weight={600} sx={{ whiteSpace: 'nowrap' }}>
                                    {metricDisplayLabel(h.metric)}
                                  </Text>
                                  {h.sortField !== '' && sort?.field === h.sortField
                                    ? (sort.dir === 'asc' ? <IconArrowUp size="0.8rem" /> : <IconArrowDown size="0.8rem" />)
                                    : null}
                                </Flex>
                              </UnstyledButton>
                            </Flex>
                          </th>
                        )}
                      </Draggable>
                    ))}
                    {dropProvided.placeholder}
                  </tr>
                )}
              </Droppable>
            </thead>
            <tbody>
              {renderRows(visibleRows)}
              {hiddenRows.length > 0 && (
                <HiddenRowsToggle
                  count={hiddenRows.length}
                  open={hiddenOpen}
                  onToggle={() => setHiddenOpen((o) => !o)}
                  colSpan={totalCols}
                  borderColor={groupDividerColor}
                />
              )}
              {hiddenOpen && renderRows(hiddenRows)}
            </tbody>
          </Table>
        </DragDropContext>
      </ScrollArea>
      )}

      {totalRecords > 0 && (
        <Flex justify="space-between" align="center" mt="sm" wrap="wrap" gap="xs">
          <Text size="xs" color="dimmed">
            {`Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, totalRecords)} of ${totalRecords}`}
          </Text>
          {totalPages > 1 && (
            <Pagination.Root total={totalPages} value={page} onChange={setPage}>
              <Group spacing={5} position="right">
                <Pagination.First />
                <Pagination.Previous />
                <Pagination.Items />
                <Pagination.Next />
                <Pagination.Last />
              </Group>
            </Pagination.Root>
          )}
        </Flex>
      )}

      {loading && (visibleRows.length > 0 || hiddenRows.length > 0) && (
        <Flex
          align="center"
          justify="center"
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: theme.colorScheme === 'dark'
              ? 'rgba(20, 21, 23, 0.5)'
              : 'rgba(255, 255, 255, 0.5)',
            pointerEvents: 'none',
            borderRadius: 8,
            // Above the pinned table cells, which carry their own z-index.
            zIndex: 5,
          }}
        >
          <Loader size="md" />
        </Flex>
      )}

      {level === 'ad' && (
        <AggregateAdKeywordDrawer
          opened={adKeywordOpen}
          onClose={() => setAdKeywordOpen(false)}
          params={adKeywordParams}
          onExport={onKeywordExport}
        />
      )}

      {/* The ad group's button sits on the Ad Set column at both levels, so the drawer mounts at both. */}
      {(level === 'adset' || level === 'ad') && (
        <AggregateKeywordDrawer
          opened={keywordOpen}
          onClose={() => setKeywordOpen(false)}
          params={keywordParams}
          onExport={onKeywordExport}
        />
      )}

      {velocityContext && (
        <ReportVelocityDrawer
          report_id={velocityContext.report_id}
          showVelocityDrawer={velocityOpen}
          setShowVelocityDrawer={setVelocityOpen}
          campaignGoalId=""
          encodeCampaignName=""
          selectedCampaignName=""
          adsetName=""
          trafficSource=""
          campaign_id=""
          trafficLevel={level === 'ad' ? 'ads' : level}
          goalType=""
          combinedGoalType={false}
          selectedAttributionModal={attributionUsed || velocityContext.attribution_model}
          displayCampaignName={velocityDisplayName}
          velocityMode="aggregate"
          aggregateVelocityParams={velocityParams}
        />
      )}
    </Paper>
  );
}
